import type { FundListDetail, FundListItemView } from '@nemo/api';
import {
  FUND_CSV_COLUMNS,
  LIST_COLUMNS,
  NOTE_KEY,
  isListColumnKey,
  throughWords,
} from '@nemo/core/model';
import { type PicklistOption, singlePicklistCell } from '../grid/cells/index.js';
import {
  FUND_COLUMNS,
  type FundColumnDef,
  type FundGridContext,
  readSelection,
  readText,
  text,
} from '../grid/fundColumns.js';
import { parseTextValue } from '../grid/paste.js';

/** The empty choice in both intro pickers: nobody suggested yet. */
const NOBODY: PicklistOption = { id: '', label: '— nobody' };

function itemOf(fundId: string, context: FundGridContext): FundListItemView | undefined {
  return context.listItems?.get(fundId);
}

function knownByHint(partner: FundListItemView['partners'][number], name: string): string {
  const carried = partner.knownThrough.find((edge) => edge.memberName === name);
  return carried === undefined ? name : `${name} (${throughWords(carried.through)})`;
}

/**
 * The fund's people, strongest first as the grid's who-knows column orders
 * them; the ones nobody knows follow under a rule.
 */
function partnerOptions(item: FundListItemView): PicklistOption[] {
  const firstUnknown = item.partners.findIndex((partner) => partner.knownBy.length === 0);
  return [
    NOBODY,
    ...item.partners.map((partner, index) => ({
      id: partner.investorId,
      label: partner.name,
      hint:
        partner.knownBy.length === 0
          ? (partner.title ?? undefined)
          : `knows ${partner.knownBy.map((name) => knownByHint(partner, name)).join(', ')}`,
      dividerBefore: index === firstUnknown && index > 0,
    })),
  ];
}

/**
 * The members who know somebody at the fund, strongest first, then every
 * other active member under a rule: the intro is usually theirs, but not
 * always, and a picker that only offers who it knows is a picker to fight.
 */
function memberOptions(item: FundListItemView, context: FundGridContext): PicklistOption[] {
  const knowing = new Set<string>(item.knownByMembers.map((member) => member.id));
  const options: PicklistOption[] = [
    NOBODY,
    ...item.knownByMembers.map((member) => ({
      id: member.id,
      label: member.name,
      hint: 'knows someone there',
    })),
    ...context.members
      .filter((member) => !knowing.has(member.id))
      .map((member, index) => ({ ...member, dividerBefore: index === 0 })),
  ];
  // Somebody since gone from the team still reads as themselves, not as an id.
  if (
    item.suggestedMemberId !== null &&
    !options.some((option) => option.id === item.suggestedMemberId)
  ) {
    options.push({ id: item.suggestedMemberId, label: item.suggestedMemberName ?? '' });
  }
  return options;
}

const memberColumn: FundColumnDef = {
  id: 'suggestedMember',
  listKey: 'suggestedIntro',
  title: 'Suggested member',
  width: 170,
  cell: (fund, context) => {
    const item = itemOf(fund.id, context);
    if (item === undefined) return text('', false);
    return singlePicklistCell(item.suggestedMemberId ?? '', memberOptions(item, context));
  },
  text: (fund, context) => itemOf(fund.id, context)?.suggestedMemberName ?? '',
  itemEdit: (cell) => {
    const value = readSelection(cell, 'singlePicklist');
    return value === null ? null : { kind: 'member', memberId: value === '' ? null : value };
  },
};

const partnerColumn: FundColumnDef = {
  id: 'suggestedPartner',
  listKey: 'suggestedIntro',
  title: 'Suggested partner',
  width: 190,
  cell: (fund, context) => {
    const item = itemOf(fund.id, context);
    if (item === undefined) return text('', false);
    const options = partnerOptions(item);
    if (
      item.suggestedInvestorId !== null &&
      !options.some((option) => option.id === item.suggestedInvestorId)
    ) {
      options.push({ id: item.suggestedInvestorId, label: item.suggestedInvestorName ?? '' });
    }
    return singlePicklistCell(item.suggestedInvestorId ?? '', options);
  },
  text: (fund, context) => itemOf(fund.id, context)?.suggestedInvestorName ?? '',
  itemEdit: (cell) => {
    const value = readSelection(cell, 'singlePicklist');
    return value === null ? null : { kind: 'partner', investorId: value === '' ? null : value };
  },
};

/** Free text the list keeps per fund: the note, or one of its own columns. */
function itemTextColumn(options: {
  id: string;
  title: string;
  width: number;
  read: (item: FundListItemView) => string;
  edit: (value: string | null) => NonNullable<ReturnType<NonNullable<FundColumnDef['itemEdit']>>>;
}): FundColumnDef {
  return {
    id: options.id,
    listKey: options.id,
    title: options.title,
    width: options.width,
    cell: (fund, context) => {
      const item = itemOf(fund.id, context);
      return text(item === undefined ? '' : options.read(item), true, context.twoLine);
    },
    text: (fund, context) => {
      const item = itemOf(fund.id, context);
      return item === undefined ? '' : options.read(item);
    },
    itemEdit: (cell) => {
      const value = readText(cell);
      if (value === null) return null;
      const parsed = parseTextValue(value);
      return parsed.ok ? options.edit(parsed.value) : null;
    },
  };
}

const noteColumn = itemTextColumn({
  id: NOTE_KEY,
  title: LIST_COLUMNS.note.label,
  width: 300,
  read: (item) => item.data[NOTE_KEY] ?? '',
  edit: (value) => ({ kind: 'note', value }),
});

/** The funds grid has no Website column of its own; a list for a founder does. */
const websiteColumn: FundColumnDef = {
  id: 'website',
  listKey: 'website',
  title: LIST_COLUMNS.website.label,
  width: 200,
  cell: (fund) => text(fund.website ?? '', true),
  text: (fund) => fund.website ?? '',
  current: (fund) => ({ kind: 'edits', edits: [{ column: 'website', value: fund.website }] }),
  commit: (_fund, cell) => {
    const value = readText(cell);
    if (value === null) return null;
    const parsed = parseTextValue(value);
    return parsed.ok
      ? { kind: 'edits', edits: [{ column: 'website', value: parsed.value }] }
      : null;
  },
};

/**
 * Stages (invests) is the grid's Stages chips, which also mark where the fund
 * leads; Stages (leads) is that half alone, as text, for a list that shows it
 * as its own column the way its export does.
 */
function stagesColumns(byId: ReadonlyMap<string, FundColumnDef>): Map<string, FundColumnDef> {
  const columns = new Map<string, FundColumnDef>();
  const chips = byId.get('stages');
  if (chips !== undefined) {
    // A list's export is the list's own columns, one key each.
    const { exports: _twoColumns, ...one } = chips;
    columns.set('stagesInvests', {
      ...one,
      listKey: 'stagesInvests',
      title: LIST_COLUMNS.stagesInvests.label,
    });
  }
  columns.set('stagesLeads', {
    id: 'stagesLeads',
    listKey: 'stagesLeads',
    title: LIST_COLUMNS.stagesLeads.label,
    width: 160,
    cell: (fund) => text(FUND_CSV_COLUMNS.stagesLeads.text(fund), false),
    text: (fund) => FUND_CSV_COLUMNS.stagesLeads.text(fund),
  });
  return columns;
}

/**
 * The list's column set as grid columns, in the list's order. The fund's own
 * columns are the funds grid's, edits and all; Suggested intro is two pickers;
 * Note and the list's extra columns are text the list keeps per fund.
 */
export function listGridColumns(list: FundListDetail): FundColumnDef[] {
  const byId = new Map(FUND_COLUMNS.map((column) => [column.id, column]));
  const extras = new Map(list.extraColumns.map((column) => [column.key, column]));
  const stages = stagesColumns(byId);
  return list.columns.flatMap((key): FundColumnDef[] => {
    if (key === 'suggestedIntro') return [memberColumn, partnerColumn];
    const stage = stages.get(key);
    if (stage !== undefined) return [stage];
    if (key === NOTE_KEY) return [noteColumn];
    if (key === 'website') return [websiteColumn];
    const extra = extras.get(key);
    if (extra !== undefined) {
      return [
        itemTextColumn({
          id: extra.key,
          title: extra.label,
          width: 200,
          read: (item) => item.data[extra.key] ?? '',
          edit: (value) => ({ kind: 'extra', key: extra.key, value }),
        }),
      ];
    }
    const column = byId.get(key);
    if (column === undefined || !isListColumnKey(key)) return [];
    // The list's own label: "Leads" and "Owners" read better to a founder
    // than the grid's working titles.
    return [{ ...column, listKey: key, title: LIST_COLUMNS[key].label }];
  });
}

/** The list keys of grid columns in their new order, each once: what a dragged header saves. */
export function listKeysOf(columns: readonly FundColumnDef[]): string[] {
  return [
    ...new Set(columns.flatMap((column) => (column.listKey === undefined ? [] : [column.listKey]))),
  ];
}
